
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "@/types/subscription";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface UserSubscriptionEditorProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PlanType = "free" | "trial" | "starter" | "growth" | "business" | "custom" | "essentials" | "scale";

interface SubscriptionUpdateData {
  plan?: PlanType;
  created_at?: string;
  company_resolutions_used?: number;
  person_resolutions_used?: number;
  metadata?: Record<string, any>;
}

export function UserSubscriptionEditor({ 
  userId,
  open,
  onOpenChange
}: UserSubscriptionEditorProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<SubscriptionUpdateData>({
    metadata: {}
  });
  const [loading, setIsLoading] = useState(false);
  const [hasLastResetDate, setHasLastResetDate] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch user subscription details
  const { data: subscription, isLoading } = useQuery({
    queryKey: ["user-subscription", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (error) {
        console.error("Error fetching subscription:", error);
        if (error.message.includes('No rows found')) {
          return null; // User has no subscription
        }
        throw error;
      }

      return data;
    },
    enabled: open
  });

  // Fetch available plans
  const { data: planConfigs } = useQuery({
    queryKey: ["plan-configs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plan_configs")
        .select("*");

      if (error) {
        console.error("Error fetching plan configs:", error);
        throw error;
      }

      return data;
    }
  });

  // Update form data when subscription is loaded
  useEffect(() => {
    if (subscription) {
      // Safely process the metadata
      const metadata = subscription.metadata && 
        typeof subscription.metadata === 'object' && 
        !Array.isArray(subscription.metadata) ? 
        subscription.metadata : {};
      
      // Check if last_reset_at exists in metadata
      const hasReset = metadata.last_reset_at ? true : false;
      setHasLastResetDate(hasReset);
      
      setFormData({
        plan: subscription.plan as PlanType,
        created_at: formatDateForInput(new Date(subscription.created_at)),
        company_resolutions_used: subscription.company_resolutions_used,
        person_resolutions_used: subscription.person_resolutions_used,
        metadata: {
          last_reset_at: hasReset 
            ? formatDateForInput(new Date(metadata.last_reset_at as string)) 
            : undefined,
          custom_resolutions: metadata.custom_resolutions || 0,
          custom_person_resolutions: metadata.custom_person_resolutions || 0
        }
      });
    }
  }, [subscription]);

  // Update subscription mutation
  const updateSubscription = useMutation({
    mutationFn: async (data: SubscriptionUpdateData) => {
      setIsLoading(true);
      try {
        // Process dates from string to ISO format
        const processedData: Record<string, any> = {
          ...data
        };
        
        if (data.created_at) {
          processedData.created_at = new Date(data.created_at).toISOString();
        }
        
        if (data.metadata) {
          const metadata: Record<string, any> = { ...data.metadata };
          
          if (hasLastResetDate && metadata.last_reset_at) {
            metadata.last_reset_at = new Date(metadata.last_reset_at).toISOString();
          } else if (!hasLastResetDate) {
            // Don't include last_reset_at if it wasn't set
            delete metadata.last_reset_at;
          }
          
          processedData.metadata = metadata;
        }

        const { data: result, error } = await supabase
          .from("subscriptions")
          .update(processedData)
          .eq("user_id", userId);

        if (error) throw error;
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      toast.success("Subscription updated successfully");
      queryClient.invalidateQueries({ queryKey: ["user-subscription", userId] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update subscription: ${error.message}`);
    }
  });

  // Helper function to format date for input field
  function formatDateForInput(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert string numbers to actual numbers
    const processedData = {
      ...formData,
      company_resolutions_used: Number(formData.company_resolutions_used),
      person_resolutions_used: Number(formData.person_resolutions_used),
      metadata: {
        ...formData.metadata,
        custom_resolutions: Number(formData.metadata?.custom_resolutions),
        custom_person_resolutions: Number(formData.metadata?.custom_person_resolutions)
      }
    };
    
    updateSubscription.mutate(processedData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith("metadata.")) {
      const metadataField = name.split(".")[1];
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          [metadataField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handlePlanChange = (plan: PlanType) => {
    setFormData({
      ...formData,
      plan
    });
    setDropdownOpen(false);
  };

  const handleLastResetDateChange = (date: Date | undefined) => {
    if (date) {
      setHasLastResetDate(true);
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          last_reset_at: formatDateForInput(date)
        }
      });
    }
  };

  // Handle toggling last reset date on/off
  const toggleLastResetDate = () => {
    if (hasLastResetDate) {
      // Remove the last reset date
      setHasLastResetDate(false);
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          last_reset_at: undefined
        }
      });
    } else {
      // Add current date as last reset date
      setHasLastResetDate(true);
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          last_reset_at: formatDateForInput(new Date())
        }
      });
    }
  };

  // Plans options array for the custom dropdown
  const planOptions: { value: PlanType; label: string }[] = [
    { value: "trial", label: "Trial" },
    { value: "free", label: "Free" },
    { value: "starter", label: "Starter" },
    { value: "growth", label: "Growth" },
    { value: "business", label: "Business" },
    { value: "custom", label: "Custom" },
    { value: "essentials", label: "Essentials" },
    { value: "scale", label: "Scale" }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit User Subscription</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="user_id">User ID</Label>
              <Input id="user_id" value={userId} disabled />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plan">Plan</Label>
                {/* Custom plan dropdown using divs instead of Radix Select */}
                <div className="relative" ref={dropdownRef}>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex justify-between items-center w-full"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span>{planOptions.find(p => p.value === formData.plan)?.label || "Select a plan"}</span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                  
                  {dropdownOpen && (
                    <div className="absolute w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 z-[9999]">
                      {planOptions.map(option => (
                        <div
                          key={option.value}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                          onClick={() => handlePlanChange(option.value)}
                        >
                          {formData.plan === option.value && (
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4">
                              <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                            </svg>
                          )}
                          <span>{option.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="created_at">Created At</Label>
                <Input
                  id="created_at"
                  name="created_at"
                  type="date"
                  value={formData.created_at}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_resolutions_used">Company Resolutions Used</Label>
                <Input
                  id="company_resolutions_used"
                  name="company_resolutions_used"
                  type="number"
                  value={formData.company_resolutions_used}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="person_resolutions_used">Person Resolutions Used</Label>
                <Input
                  id="person_resolutions_used"
                  name="person_resolutions_used"
                  type="number"
                  value={formData.person_resolutions_used}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="last_reset_at">Last Reset At</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs"
                  onClick={toggleLastResetDate}
                >
                  {hasLastResetDate ? "Remove date" : "Set date"}
                </Button>
              </div>
              
              {hasLastResetDate ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      {formData.metadata?.last_reset_at ? (
                        format(new Date(formData.metadata.last_reset_at), "PPP")
                      ) : (
                        <span>Select date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.metadata?.last_reset_at ? new Date(formData.metadata.last_reset_at) : undefined}
                      onSelect={handleLastResetDateChange}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal text-muted-foreground"
                  onClick={toggleLastResetDate}
                >
                  <span>No reset date set</span>
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="custom_resolutions">Custom Resolutions</Label>
                <Input
                  id="custom_resolutions"
                  name="metadata.custom_resolutions"
                  type="number"
                  value={formData.metadata?.custom_resolutions}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="custom_person_resolutions">Custom Person Resolutions</Label>
                <Input
                  id="custom_person_resolutions"
                  name="metadata.custom_person_resolutions"
                  type="number"
                  value={formData.metadata?.custom_person_resolutions}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
